import { Head, Link } from "@inertiajs/react"
import { Gift } from "lucide-react"
import { useTranslation } from "react-i18next"

import type { RedeemProfileAddress } from "@/components/coins/redeem-ship-to"
import {
  type RedemptionHistoryItem,
  RedemptionHistoryRow,
} from "@/components/creator/rewards/redemption-history-row"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { creatorRewardsPath } from "@/routes"

/**
 * OKS-407 — Creator "My rewards" history (`/rewards/mine`, board
 * `594:415` / empty `595:440`). NET-NEW surface: a vertical list of the
 * creator's own redemptions for the CURRENT brand, scoped server-side to
 * `Current.membership.id` (brand-implicit). Empty → dashed Gift card +
 * "Browse rewards" CTA back to the catalog.
 */

interface BrandShape {
  id: number
  name: string
}

export interface MyRewardsPageProps {
  /** OKS-985 — prefill for the legacy-address drawer (UX Spec §2.4). */
  profile_address: RedeemProfileAddress | null
  redemptions: RedemptionHistoryItem[]
  brand: BrandShape
}

export default function MyRewards({
  redemptions,
  brand,
  profile_address,
}: MyRewardsPageProps) {
  const { t } = useTranslation()
  const hasRedemptions = redemptions.length > 0

  return (
    <AppLayout>
      <Head title={t("rewards.mine.head_title")} />

      <div className="container mx-auto max-w-2xl space-y-5 px-4 py-6">
        <header data-testid="creator-my-rewards-header">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("rewards.mine.title")}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("rewards.mine.subtitle", { brand: brand.name })}
          </p>
        </header>

        {hasRedemptions ? (
          <div className="space-y-3" data-testid="creator-my-rewards-list">
            {redemptions.map((redemption) => (
              <RedemptionHistoryRow
                key={redemption.id}
                redemption={redemption}
                profileAddress={profile_address}
              />
            ))}
          </div>
        ) : (
          <Card
            className="border-dashed py-12"
            data-testid="creator-my-rewards-empty-state"
          >
            <CardContent className="flex flex-col items-center justify-center gap-3 text-center">
              <Gift
                aria-hidden="true"
                className="text-muted-foreground size-12"
              />
              <h2 className="text-lg font-semibold">
                {t("rewards.mine.empty_title")}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t("rewards.mine.empty_body")}
              </p>
              <Link
                href={creatorRewardsPath()}
                data-testid="creator-my-rewards-browse-link"
                className="text-primary hover:text-primary/80 text-sm font-medium underline-offset-4 hover:underline"
              >
                {t("rewards.mine.browse")}
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  )
}
